/**
 * TokenList component displays tokens in different categories
 * Now uses DIRECT SolanaTracker WebSocket connections for real-time token discovery
 * Bypasses Supabase entirely for immediate token updates
 */

import React, { useEffect, useState, useRef, useCallback, useMemo } from 'react';
import { Token } from '../types/supabase';
import TokenCard from './TokenCard';
import { Loader, AlertCircle, RefreshCw, Pause } from 'lucide-react';
import axios from 'axios';
import websocketService from '../services/websocketService';
import { getTokenTransactions } from '../services/solanatrackerService';

interface TokenListProps {
  category: 'new' | 'trending' | 'migrated' | 'graduating' | 'graduated';
  searchTerm?: string;
  onPauseFeed?: () => void;
  onResumeFeed?: () => void;
  containerClassName?: string;
}

const PAGE_SIZE = 25;
const MAX_DISPLAY_TOKENS = 25;
const MAX_SUBSCRIBED_TOKENS = 100;

// 🔥 **MARK CAP FILTER CONFIG**: Maximum market cap allowed in "new" token feeds, minimum for "trending"
const MAX_NEW_TOKEN_MARKET_CAP = 100000; // $100,000 USD - tokens above this won't appear in "new" feeds
const MIN_TRENDING_TOKEN_MARKET_CAP = 30000; // $30,000 USD - tokens below this won't appear in "trending" feeds

// 🔥 **TRENDING TOKEN CRITERIA**: 
// - Curve percentage > 25% OR Market cap > $30,000
// - Must be < 100% curve (not yet migrated)
// - Either condition qualifies for trending

// 🔥 **PERFORMANCE**: Memoized TokenCard to prevent unnecessary re-renders
const MemoizedTokenCard = React.memo(TokenCard, (prevProps, nextProps) => {
  // Only re-render if essential data actually changed
  const prevToken = prevProps.token;
  const nextToken = nextProps.token;
  
  // If it's the same mint address, check for meaningful changes only
  if (prevToken.mint === nextToken.mint) {
    const shouldUpdate = (
      prevToken.price_usd !== nextToken.price_usd ||
      prevToken.market_cap_usd !== nextToken.market_cap_usd ||
      prevToken.buys_count !== nextToken.buys_count ||
      prevToken.sells_count !== nextToken.sells_count ||
      prevToken.bonding_curve_progress !== nextToken.bonding_curve_progress ||
      prevToken.volume_usd !== nextToken.volume_usd ||
      prevToken.holders_count !== nextToken.holders_count ||
      prevToken.dev_sell_time !== nextToken.dev_sell_time ||
      prevProps.category !== nextProps.category
    );
    
    // Return true to SKIP render (no changes), false to allow render (has changes)
    return !shouldUpdate;
  }
  
  // Different tokens, allow render
  return false;
});

// Local base64 placeholder image
const PLACEHOLDER_IMAGE = "data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNjQiIGhlaWdodD0iNjQiIHZpZXdCb3g9IjAgMCA2NCA2NCIgZmlsbD0ibm9uZSIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj4KPHJlY3Qgd2lkdGg9IjY0IiBoZWlnaHQ9IjY0IiByeD0iMzIiIGZpbGw9IiMxNjE2MUEiLz4KPHBhdGggZD0iTTQxLjggMjMuNzVIMjEuODVWNDAuMjVINDEuOFYyMy43NVoiIHN0cm9rZT0iIzM4Q0Q5QSIgc3Ryb2tlLXdpZHRoPSIyIi8+Cjwvc3ZnPg==";

// 🔧 **TOKEN CACHE** - Maintains tokens per category
const tokenCache: Record<string, Token[]> = {
  new: [],
  trending: [],
  migrated: [],
  graduating: [],
  graduated: []
};

// 🔧 **CATEGORY LOADING STATES**
const categoryLoadingStates: Record<string, boolean> = {
  new: true,
  trending: true, 
  migrated: true,
  graduating: true,
  graduated: true
};

// 🔥 **ROOM NAME MAPPING**
const getCategoryRoomName = (category: string): string => {
  switch (category) {
    case 'new':
      return 'latest';
    case 'trending':
      return 'graduating';
    case 'migrated':
      return 'graduated';
    case 'graduating':
      return 'graduating';
    case 'graduated':
      return 'graduated';
    default:
      return 'latest';
  }
};

// 🔥 **SOLANATRACKER TO TOKEN TRANSFORMATION**
const transformSolanaTrackerToken = async (data: any): Promise<Token | null> => {
  try {
    let tokenData: any = null;
    let poolData: any = null;
    
    // Handle different SolanaTracker data structures
    if (data && data.token && data.pools) {
      tokenData = data.token;
      poolData = data.pools?.[0];
    } else if (data && data.token) {
      tokenData = data.token;
      poolData = data.pool || null;
    } else if (data && data.mint) {
      tokenData = data;
      poolData = data.pools?.[0] || data.pool || null;
    } else if (data && data.data) {
      return transformSolanaTrackerToken(data.data);
    }
    
    if (!tokenData || !tokenData.mint) {
      console.warn('Invalid token data structure received');
        return null;
      }
      
    // 🔍 **COMPREHENSIVE DEBUG**: Log all data to understand structure
    console.log(`🔍 FULL DEBUG - Token ${tokenData.symbol || 'UNKNOWN'}:`, {
      mint: tokenData.mint?.slice(0, 8),
      fullData: data,
      tokenData: tokenData,
      poolData: poolData,
      // Check all possible curve percentage fields
      curvePercentage: poolData?.curvePercentage,
      lpBurn: poolData?.lpBurn,
      bondingCurve: poolData?.bondingCurve,
      curve: poolData?.curve,
      progress: poolData?.progress,
      percentage: poolData?.percentage,
      // Check if it's nested differently
      pool_curvePercentage: data.pool?.curvePercentage,
      pool_lpBurn: data.pool?.lpBurn,
      // Check if it's in the root data
      data_curvePercentage: data.curvePercentage,
      data_lpBurn: data.lpBurn,
      data_bondingCurve: data.bondingCurve,
      // 🔍 **MARKET CAP DEBUG**: Check all possible market cap fields
      poolData_marketCap_usd: poolData?.marketCap?.usd,
      poolData_marketCap: poolData?.marketCap,
      poolData_market_cap: poolData?.market_cap,
      poolData_mcap: poolData?.mcap,
      data_marketCap_usd: data.marketCap?.usd,
      data_marketCap: data.marketCap,
      data_market_cap: data.market_cap,
      data_mcap: data.mcap,
      pools_0_marketCap_usd: data.pools?.[0]?.marketCap?.usd,
      pools_0_marketCap: data.pools?.[0]?.marketCap,
      pools_0_market_cap: data.pools?.[0]?.market_cap,
      pools_0_mcap: data.pools?.[0]?.mcap,
      tokenData_marketCap: tokenData?.marketCap,
      tokenData_market_cap: tokenData?.market_cap,
      tokenData_mcap: tokenData?.mcap
    });

    // Transform to our Token format
    const transformedToken: Token = {
      id: tokenData.mint,
      mint: tokenData.mint,
      symbol: tokenData.symbol || 'UNKNOWN',
      name: tokenData.name || 'Unknown Token',
      
      // Image handling
      image_url: tokenData.image && typeof tokenData.image === 'string' && tokenData.image.startsWith('http') 
        ? tokenData.image 
        : tokenData.imageUri && typeof tokenData.imageUri === 'string' && tokenData.imageUri.startsWith('http')
        ? tokenData.imageUri
        : null,
      image_uri: tokenData.imageUri && typeof tokenData.imageUri === 'string' && tokenData.imageUri.startsWith('http')
        ? tokenData.imageUri
        : tokenData.image && typeof tokenData.image === 'string' && tokenData.image.startsWith('http')
        ? tokenData.image
        : null,
      uri: tokenData.uri || null,
      
      // Timestamps
      created_at: poolData?.createdAt ? new Date(poolData.createdAt).toISOString() : new Date().toISOString(),
      received_at: new Date().toISOString(),
      discovery_timestamp: new Date().toISOString(),
      
      // Price and market data
      price_usd: poolData?.price?.usd || 0,
      market_cap_usd: (() => {
        // 🔥 **ENHANCED INITIAL MARKET CAP DETECTION**: Try multiple possible fields and structures
        const possibleMarketCapFields = [
          // Direct fields - all variations
          poolData?.marketCap?.usd,
          poolData?.market_cap?.usd,
          poolData?.marketcap?.usd,
          poolData?.marketCapUsd,
          poolData?.market_cap_usd,
          poolData?.marketcapusd,
          poolData?.marketCap,
          poolData?.market_cap,
          poolData?.marketcap,
          poolData?.mcap,
          poolData?.mc,
          
          // Check in data object
          data.marketCap?.usd,
          data.market_cap?.usd,
          data.marketcap?.usd,
          data.marketCapUsd,
          data.market_cap_usd,
          data.marketcapusd,
          data.marketCap,
          data.market_cap,
          data.marketcap,
          data.mcap,
          data.mc,
          
          // Check in pools array
          data.pools?.[0]?.marketCap?.usd,
          data.pools?.[0]?.market_cap?.usd,
          data.pools?.[0]?.marketcap?.usd,
          data.pools?.[0]?.marketCapUsd,
          data.pools?.[0]?.market_cap_usd,
          data.pools?.[0]?.marketcapusd,
          data.pools?.[0]?.marketCap,
          data.pools?.[0]?.market_cap,
          data.pools?.[0]?.marketcap,
          data.pools?.[0]?.mcap,
          data.pools?.[0]?.mc,
          
          // Check in token data
          tokenData?.marketCap?.usd,
          tokenData?.market_cap?.usd,
          tokenData?.marketcap?.usd,
          tokenData?.marketCapUsd,
          tokenData?.market_cap_usd,
          tokenData?.marketcapusd,
          tokenData?.marketCap,
          tokenData?.market_cap,
          tokenData?.marketcap,
          tokenData?.mcap,
          tokenData?.mc
        ];
        
        // Find the first valid market cap value
        for (const field of possibleMarketCapFields) {
          if (typeof field === 'number' && field > 0) {
            console.log(`✅ Found initial market cap: $${field.toLocaleString()} for ${tokenData.symbol}`);
            return field;
          }
        }
        
        console.log(`❌ No initial market cap found for ${tokenData.symbol}`);
        return 0;
      })(),
      liquidity_usd: poolData?.liquidity?.usd || 0,
      volume_usd: poolData?.volume?.usd || poolData?.txns?.volume || 0,
      tx_volume: poolData?.volume?.usd || poolData?.txns?.volume || 0,
      
      // Transaction counts - start with 0, will be updated from API
      buys_count: 0,
      sells_count: 0,
      buys: 0,
      sells: 0,
      volume: poolData?.volume?.usd || poolData?.txns?.volume || 0,
      
      // Additional data
      holders_count: data.holders || 0,
      
      // 🔥 **ENHANCED CURVE PERCENTAGE DETECTION**: Try multiple possible fields and structures
      bonding_curve_progress: (() => {
        // Try all possible curve percentage fields
        const possibleFields = [
          poolData?.curvePercentage,
          poolData?.lpBurn,
          poolData?.bondingCurve,
          poolData?.curve,
          poolData?.progress,
          poolData?.percentage,
          data.pool?.curvePercentage,
          data.pool?.lpBurn,
          data.curvePercentage,
          data.lpBurn,
          data.bondingCurve,
          data.curve,
          data.progress,
          data.percentage
        ];
        
        // Find the first valid number
        for (const field of possibleFields) {
          if (typeof field === 'number' && field >= 0 && field <= 100) {
            console.log(`✅ Found curve percentage: ${field}% for ${tokenData.symbol}`);
            return field;
          }
        }
        
        console.log(`❌ No curve percentage found for ${tokenData.symbol}`);
        return 0;
      })(),
      
      market: poolData?.market || 'pump',
      market_type: poolData?.market || 'pump',
      is_rugged: false,
      
      // Social links
      description: tokenData.description || null,
      twitter: tokenData.twitter && typeof tokenData.twitter === 'string' && tokenData.twitter.includes('twitter') 
        ? tokenData.twitter 
        : tokenData.strictSocials?.twitter || null,
      telegram: tokenData.telegram && typeof tokenData.telegram === 'string' && tokenData.telegram.includes('telegram') 
        ? tokenData.telegram 
        : tokenData.strictSocials?.telegram || null,
      website: tokenData.website && typeof tokenData.website === 'string' && tokenData.website.startsWith('http') 
        ? tokenData.website 
        : tokenData.strictSocials?.website || null,
      discord: tokenData.discord && typeof tokenData.discord === 'string' && tokenData.discord.includes('discord') 
        ? tokenData.discord 
        : tokenData.strictSocials?.discord || null,
      youtube: tokenData.youtube && typeof tokenData.youtube === 'string' && tokenData.youtube.includes('youtube') 
        ? tokenData.youtube 
        : tokenData.strictSocials?.youtube || null,
      metadata: tokenData.metadata || null,
      
      // Additional fields
        signature: null,
        trader_public_key: null,
        tx_type: null,
        sol_amount: null,
        initial_buy: null,
      market_cap_sol: poolData?.marketCap?.quote || null,
        token_amount: null,
        bonding_curve_key: null,
        v_sol_in_bonding_curve: null,
        v_tokens_in_bonding_curve: null,
      timestamp: poolData?.lastUpdated ? new Date(poolData.lastUpdated).toISOString() : null,
      updated_at: poolData?.lastUpdated ? new Date(poolData.lastUpdated).toISOString() : null,
        attributes: null,
        dev_projects: null,
        rug_date: null,
        rug_type: null,
        snipers: null,
        insiders: null,
        top_10_holders: null,
        sequence_number: null,
        position: null,
      userBalance: undefined,
      dev_sell_time: null,
      liquidity: poolData?.liquidity?.usd || null,
      token_price: poolData?.price?.usd || null,
      holders: data.holders || 0,
      dev_wallet: poolData?.deployer || null,
      dev_buy_tokens: null,
      dev_buy_time: null,
      is_raydium: poolData?.market === 'raydium' || false,
      is_migrated: poolData?.market === 'raydium' || false,
      first_seen_in_trending: null,
      program: null,
      transaction_time: null,
      transaction_type: null,
      transaction_amount: null,
      transaction_price_usd: null,
      sol_volume: poolData?.volume?.quote || null,
      volume_5m: null,
      volume_15m: null,
      volume_30m: null,
      volume_1h: null,
      volume_6h: null,
      volume_12h: null,
      volume_24h: poolData?.volume?.usd || null,
      jupiter: null,
      risk: data.risk || undefined,
      events: data.events || undefined,
      pools: data.pools || null
    };

    // 🔥 **TRACK DEPLOYER ADDRESS** for dev sell detection
    const deployerAddress = poolData?.deployer || poolData?.creator || tokenData?.deployer || tokenData?.creator;
    if (deployerAddress && typeof deployerAddress === 'string') {
      tokenDeployers.set(transformedToken.mint, deployerAddress);
      console.log(`📍 Tracked deployer for ${transformedToken.symbol}: ${deployerAddress.slice(0, 8)}...`);
    } else {
      console.log(`⚠️ No deployer address found for ${transformedToken.symbol}`);
    }

    // 🔥 **FETCH METADATA IF URI EXISTS**
    if (transformedToken.uri && !transformedToken.image_url) {
      try {
        console.log(`🔍 Fetching metadata for ${transformedToken.mint.slice(0, 8)}...`);
        
        let fetchUrl = transformedToken.uri;
        if (fetchUrl.startsWith('ipfs://')) {
          fetchUrl = fetchUrl.replace('ipfs://', 'https://ipfs.io/ipfs/');
        } else if (fetchUrl.startsWith('ar://')) {
          fetchUrl = fetchUrl.replace('ar://', 'https://arweave.net/');
        }

        const response = await axios.get(fetchUrl, {
          headers: { 'Accept': 'application/json' },
          timeout: 10000
        });
        
        if (response.status === 200 && response.data) {
          const metadata = response.data;
          const imageUrl = metadata.image || metadata.image_url || metadata.image_uri;

          if (imageUrl) {
            let processedImageUrl = imageUrl.toString().replace('ipfs://', 'https://ipfs.io/ipfs/');
            if (!processedImageUrl.startsWith('http')) {
              if (processedImageUrl.startsWith('/')) {
                try {
                  const baseUrl = new URL(fetchUrl);
                  processedImageUrl = `${baseUrl.origin}${processedImageUrl}`;
                } catch {
                  processedImageUrl = `https://arweave.net${processedImageUrl}`;
                }
              } else if (processedImageUrl.startsWith('ar://')) {
                processedImageUrl = processedImageUrl.replace('ar://', 'https://arweave.net/');
              } else {
                processedImageUrl = `https://${processedImageUrl}`;
              }
            }

            transformedToken.image_url = processedImageUrl;
            transformedToken.image_uri = processedImageUrl;
          }

          // Extract social links
          if (metadata.twitter && !transformedToken.twitter) {
            transformedToken.twitter = metadata.twitter;
          }
          if (metadata.telegram && !transformedToken.telegram) {
            transformedToken.telegram = metadata.telegram;
          }
          if (metadata.discord && !transformedToken.discord) {
            transformedToken.discord = metadata.discord;
          }
          if (metadata.website && !transformedToken.website) {
            transformedToken.website = metadata.website;
          }
        }
      } catch (error) {
        console.warn(`Failed to fetch metadata for ${transformedToken.mint.slice(0, 8)}:`, error);
      }
    }

    return transformedToken;
  } catch (error) {
    console.error('Error transforming SolanaTracker token data:', error, data);
    return null;
  }
};

// 🔧 **ENHANCED GLOBAL SUBSCRIPTION MANAGER**: Maintains all WebSocket subscriptions regardless of active tab
type CategoryType = 'new' | 'trending' | 'migrated' | 'graduating' | 'graduated';

const globalSubscriptionManager = {
  initialized: false,
  subscriptions: {
    new: { active: false, unsubscribeFunctions: [] as (() => void)[] },
    trending: { active: false, unsubscribeFunctions: [] as (() => void)[] },
    migrated: { active: false, unsubscribeFunctions: [] as (() => void)[] },
    graduating: { active: false, unsubscribeFunctions: [] as (() => void)[] },
    graduated: { active: false, unsubscribeFunctions: [] as (() => void)[] }
  } as Record<CategoryType, { active: boolean; unsubscribeFunctions: (() => void)[] }>,
  
  // 🔥 **NEW**: Track individual token subscriptions across all categories
  tokenSubscriptions: new Map<string, {
    mint: string;
    symbol: string;
    name: string;
    categories: Set<CategoryType>;
    unsubscribeFunctions: (() => void)[];
    lastSeen: number;
  }>(),
  
  // 🔥 **NEW**: Maximum number of tokens to maintain subscriptions for
  MAX_TOKEN_SUBSCRIPTIONS: 200,
  
  async initializeAllSubscriptions() {
    if (this.initialized) return;
    
    console.log('🔌 Initializing enhanced global WebSocket subscriptions for all categories...');
    
    try {
      // Ensure WebSocket connection
      await websocketService.connect();
      
      // Initialize subscriptions for all categories
      const categories: CategoryType[] = ['new', 'trending', 'migrated', 'graduating', 'graduated'];
      
      for (const category of categories) {
        await this.setupCategorySubscription(category);
      }
      
      this.initialized = true;
      console.log('✅ All enhanced global WebSocket subscriptions initialized');
      
    } catch (error) {
      console.error('❌ Failed to initialize enhanced global subscriptions:', error);
    }
  },
  
  async setupCategorySubscription(category: CategoryType) {
    if (this.subscriptions[category]?.active) {
      console.log(`⏭️ Category ${category} already has active subscription`);
      return;
    }
    
    const roomName = getCategoryRoomName(category);
    console.log(`📡 Setting up enhanced global subscription for ${category} → ${roomName}`);
    
    try {
      // Join the room
      websocketService.joinRoom(roomName);
      
      // Subscribe to category-specific token updates
      const categoryUnsubscribe = websocketService.on(roomName, async (data: any) => {
        try {
          const transformedToken = await transformSolanaTrackerToken(data);
          
          if (transformedToken) {
            // Apply market cap filter for new and trending feeds
            if (category === 'new' || category === 'trending') {
              const marketCapUSD = transformedToken.market_cap_usd || 0;
              
              if (category === 'new' && marketCapUSD > MAX_NEW_TOKEN_MARKET_CAP) {
                console.log(`🚫 Global filter: Blocking ${transformedToken.symbol} from ${category} feed - Market cap: $${marketCapUSD.toLocaleString()}`);
                return;
              }
              
              if (category === 'trending' && marketCapUSD < MIN_TRENDING_TOKEN_MARKET_CAP) {
                console.log(`🚫 Global filter: Blocking ${transformedToken.symbol} from ${category} feed - Market cap too low: $${marketCapUSD.toLocaleString()} (min: $${MIN_TRENDING_TOKEN_MARKET_CAP.toLocaleString()})`);
                return;
              }
            }
            
            console.log(`🆕 Enhanced global feed update [${category}]: ${transformedToken.symbol} (${transformedToken.mint.slice(0, 8)}...) - Curve: ${transformedToken.bonding_curve_progress}%, Market Cap: $${(transformedToken.market_cap_usd || 0).toLocaleString()}`);
            
            // Update the appropriate category cache
            const currentTokens = tokenCache[category] || [];
            const existingIndex = currentTokens.findIndex(t => t.mint === transformedToken.mint);
            
            if (existingIndex >= 0) {
              // Update existing token
              const updatedTokens = [...currentTokens];
              updatedTokens[existingIndex] = {
                ...updatedTokens[existingIndex],
                ...transformedToken
              };
              tokenCache[category] = updatedTokens.slice(0, MAX_DISPLAY_TOKENS);
            } else {
              // Add new token at the beginning
              const newTokens = [transformedToken, ...currentTokens].slice(0, MAX_DISPLAY_TOKENS);
              tokenCache[category] = newTokens;
              
              // 🔥 **CHECK TRENDING CRITERIA ON NEW TOKENS**: Check if new tokens immediately qualify for trending
              if (category === 'new') {
                const curvePercentage = transformedToken.bonding_curve_progress || 0;
                const marketCapUSD = transformedToken.market_cap_usd || 0;
                
                // 🔥 **OR LOGIC**: Either curve > 25% OR market cap > $30,000 qualifies for trending
                if (curvePercentage < 100 && (curvePercentage > 25 || marketCapUSD > MIN_TRENDING_TOKEN_MARKET_CAP)) {
                  console.log(`🔥 NEW TOKEN QUALIFIES FOR TRENDING: ${transformedToken.symbol} - Curve: ${curvePercentage}%, Market Cap: $${marketCapUSD.toLocaleString()} (Either condition met)`);
                  
                  // Add to trending feed immediately
                  const currentTrendingTokens = tokenCache['trending'] || [];
                  const trendingExists = currentTrendingTokens.find(t => t.mint === transformedToken.mint);
                  
                  if (!trendingExists) {
                    // Mark as entered trending (start grace period)
                    markTokenEnteredTrending(transformedToken.mint, transformedToken.symbol || 'UNKNOWN');
                    
                    const newTrendingTokens = [transformedToken, ...currentTrendingTokens].slice(0, MAX_DISPLAY_TOKENS);
                    tokenCache['trending'] = newTrendingTokens;
                    
                    // Dispatch event for trending category
                    window.dispatchEvent(new CustomEvent('ekko:token_feed_update', {
                      detail: { category: 'trending', token: transformedToken, action: 'add' }
                    }));
                  }
                }
              }
            }
            
            // 🔥 **MARK TRENDING TOKENS**: If this is directly from trending feed, mark grace period
            if (category === 'trending') {
              markTokenEnteredTrending(transformedToken.mint, transformedToken.symbol || 'UNKNOWN');
            }
            
            // 🔥 **NEW**: Automatically subscribe to individual token updates
            if (transformedToken.mint && transformedToken.name && transformedToken.symbol) {
              await this.subscribeToTokenUpdates(transformedToken.mint, transformedToken.name, transformedToken.symbol, category);
            }
            
            // Dispatch event for active components to update
            window.dispatchEvent(new CustomEvent('ekko:token_feed_update', {
              detail: { category, token: transformedToken, action: 'add' }
            }));
          }
        } catch (error) {
          console.error(`Error in enhanced global ${category} subscription:`, error);
        }
      });
      
      // Store the unsubscribe function
      this.subscriptions[category].unsubscribeFunctions.push(categoryUnsubscribe);
      this.subscriptions[category].active = true;
      
      console.log(`✅ Enhanced global subscription active for ${category}`);
      
  } catch (error) {
      console.error(`❌ Failed to setup enhanced global subscription for ${category}:`, error);
    }
  },
  
  // 🔥 **NEW**: Subscribe to individual token updates globally
  async subscribeToTokenUpdates(tokenMint: string, tokenName: string, tokenSymbol: string, category: CategoryType) {
    // Check if we already have a subscription for this token
    const existingSubscription = this.tokenSubscriptions.get(tokenMint);
    
    if (existingSubscription) {
      // Add this category to the existing subscription
      existingSubscription.categories.add(category);
      existingSubscription.lastSeen = Date.now();
      console.log(`📡 Added category ${category} to existing subscription for ${tokenSymbol}`);
      return;
    }
    
    // Check if we're at the subscription limit
    if (this.tokenSubscriptions.size >= this.MAX_TOKEN_SUBSCRIPTIONS) {
      // Remove oldest subscriptions to make room
      const sortedByLastSeen = Array.from(this.tokenSubscriptions.entries())
        .sort((a, b) => a[1].lastSeen - b[1].lastSeen);
      
      const toRemove = sortedByLastSeen.slice(0, 10); // Remove 10 oldest
      toRemove.forEach(([mint, subscription]) => {
        console.log(`🧹 Removing old token subscription for ${subscription.symbol} to make room`);
        subscription.unsubscribeFunctions.forEach(unsub => {
          try {
            unsub();
          } catch (error) {
            console.error('Error unsubscribing from old token:', error);
          }
        });
        this.tokenSubscriptions.delete(mint);
      });
    }
    
    console.log(`📡 Creating new global subscription for ${tokenSymbol} (${tokenMint.slice(0, 8)}...)`);
    
    const tokenSubscription = {
      mint: tokenMint,
      symbol: tokenSymbol,
      name: tokenName,
      categories: new Set([category]),
      unsubscribeFunctions: [] as (() => void)[],
      lastSeen: Date.now()
    };
    
    try {
      // Subscribe to token price/pool updates
      const tokenRoomId = `token:${tokenMint}`;
      websocketService.joinRoom(tokenRoomId);
      
      const tokenUpdateUnsubscribe = websocketService.on(tokenRoomId, (data: any) => {
        this.handleTokenUpdate(tokenMint, tokenSymbol, data);
      });
      tokenSubscription.unsubscribeFunctions.push(tokenUpdateUnsubscribe);

      // Subscribe to token stats updates
      const statsRoomId = `stats:${tokenMint}`;
      websocketService.joinRoom(statsRoomId);
      
      const statsUpdateUnsubscribe = websocketService.on(statsRoomId, (data: any) => {
        this.handleStatsUpdate(tokenMint, tokenSymbol, data);
      });
      tokenSubscription.unsubscribeFunctions.push(statsUpdateUnsubscribe);

      // Subscribe to transactions
      const transactionUnsubscribe = websocketService.subscribeToTransactions(tokenMint, (data: any) => {
        this.handleTransactionUpdate(tokenMint, tokenSymbol, data);
      });
      tokenSubscription.unsubscribeFunctions.push(transactionUnsubscribe);

      // Subscribe to holder updates
      const holdersRoomId = `holders:${tokenMint}`;
      websocketService.joinRoom(holdersRoomId);
      
      const holdersUnsubscribe = websocketService.on(holdersRoomId, (data: any) => {
        this.handleHoldersUpdate(tokenMint, tokenSymbol, data);
      });
      tokenSubscription.unsubscribeFunctions.push(holdersUnsubscribe);

      // Store the subscription
      this.tokenSubscriptions.set(tokenMint, tokenSubscription);
      
      console.log(`✅ Global token subscription created for ${tokenSymbol} across categories: ${Array.from(tokenSubscription.categories).join(', ')}`);

    } catch (error) {
      console.error(`❌ Failed to create global subscription for ${tokenMint}:`, error);
    }
  },
  
  // 🔥 **ENHANCED**: Handle token updates globally - detect dev sells and increment counts
  handleTransactionUpdate(tokenMint: string, tokenSymbol: string, data: any) {
    try {
      if (!data || (typeof data !== 'object' && !Array.isArray(data))) return;

      const transactions = Array.isArray(data) ? data : [data];
      
      let buysIncrement = 0;
      let sellsIncrement = 0;
      let devSellDetected = false;
      
      transactions.forEach(txData => {
        if (!txData || typeof txData !== 'object') return;
        
        // Check for dev sell BEFORE counting the transaction
        if (checkForDevSell(tokenMint, txData)) {
          devSellDetected = true;
        }
        
        // Count the transaction
        if (txData.type === 'buy') {
          buysIncrement += 1;
        } else if (txData.type === 'sell') {
          sellsIncrement += 1;
        }
      });
      
      // Update buy/sell counts
      if (buysIncrement > 0 || sellsIncrement > 0) {
        console.log(`📊 Live transaction for ${tokenSymbol}: +${buysIncrement} buys, +${sellsIncrement} sells`);
        this.simpleIncrementCounts(tokenMint, buysIncrement, sellsIncrement);
      }
      
      // Handle dev sell if detected
      if (devSellDetected) {
        this.markTokenAsDevSold(tokenMint, tokenSymbol);
      }
    } catch (error) {
      console.error(`Error in transaction update for ${tokenMint}:`, error);
    }
  },
  
  // 🔥 **SIMPLIFIED**: Handle stats updates globally
  handleStatsUpdate(tokenMint: string, tokenSymbol: string, data: any) {
    try {
      if (!data || typeof data !== 'object') return;

      console.log(`📊 Stats update for ${tokenSymbol}`);
      
      const subscription = this.tokenSubscriptions.get(tokenMint);
      if (!subscription) return;
      
      subscription.categories.forEach(category => {
        const categoryTokens = tokenCache[category];
        if (!categoryTokens) return;
        
        const tokenIndex = categoryTokens.findIndex(t => t.mint === tokenMint);
        if (tokenIndex >= 0) {
          const token = { ...categoryTokens[tokenIndex] };
          
          // Enhanced curve percentage detection from stats
          let curvePercentageValue = this.findCurvePercentageValue(data);
          if (curvePercentageValue !== null) {
            token.bonding_curve_progress = curvePercentageValue;
          }

          // Enhanced market cap detection from stats
          let marketCapValue = this.findMarketCapValue(data);
          if (marketCapValue !== null) {
            token.market_cap_usd = marketCapValue;
          }

          if (data.volume && typeof data.volume === 'number') {
            token.volume_usd = data.volume;
            token.tx_volume = data.volume;
            token.volume = data.volume;
          }
          
          if (data.holders && typeof data.holders === 'number') {
            token.holders_count = data.holders;
            token.holders = data.holders;
          }

          token.updated_at = new Date().toISOString();
          categoryTokens[tokenIndex] = token;
          
          // 🔥 **AUTO-PROMOTION CHECK**: Check if token should be promoted to trending
          this.checkAndPromoteToTrending(token, category);
          
          // Dispatch update event
          window.dispatchEvent(new CustomEvent('ekko:token_feed_update', {
            detail: { category, token, action: 'update' }
          }));
        }
      });
    } catch (error) {
      console.error(`Error in stats update for ${tokenMint}:`, error);
    }
  },
  
  // 🔥 **SIMPLIFIED**: Handle holders updates globally
  handleHoldersUpdate(tokenMint: string, tokenSymbol: string, data: any) {
    try {
      if (!data || typeof data !== 'object') return;
      
      if (data.total !== undefined && typeof data.total === 'number' && data.total >= 0) {
        console.log(`👥 Holders update for ${tokenSymbol}: ${data.total}`);
        
        const subscription = this.tokenSubscriptions.get(tokenMint);
        if (!subscription) return;
        
        subscription.categories.forEach(category => {
          const categoryTokens = tokenCache[category];
          if (!categoryTokens) return;
          
          const tokenIndex = categoryTokens.findIndex(t => t.mint === tokenMint);
          if (tokenIndex >= 0) {
            const token = { ...categoryTokens[tokenIndex] };
            
            token.holders_count = data.total;
            token.holders = data.total;
            token.updated_at = new Date().toISOString();
            
            categoryTokens[tokenIndex] = token;
            
            // 🔥 **AUTO-PROMOTION CHECK**: Check if token should be promoted to trending
            this.checkAndPromoteToTrending(token, category);
            
            // Dispatch update event
            window.dispatchEvent(new CustomEvent('ekko:token_feed_update', {
              detail: { category, token, action: 'update' }
            }));
          }
        });
      }
    } catch (error) {
      console.error(`Error in holders update for ${tokenMint}:`, error);
    }
  },
  
  // 🔥 **ENHANCED**: Promote token from new to trending category with subscription maintenance and grace period
  promoteTokenToTrending(token: any) {
    const subscription = this.tokenSubscriptions.get(token.mint);
    if (!subscription) {
      console.log(`⚠️ No subscription found for ${token.symbol} during promotion - this token needs to be processed properly`);
      return;
    }
    
    // Add trending category to subscription
    subscription.categories.add('trending');
    console.log(`📈 Added trending category to ${token.symbol} subscription`);
    
    // Add token to trending cache
    const currentTrendingTokens = tokenCache['trending'] || [];
    const existingIndex = currentTrendingTokens.findIndex(t => t.mint === token.mint);
    
    if (existingIndex === -1) {
      // Mark token as entered trending (start grace period)
      markTokenEnteredTrending(token.mint, token.symbol);
      
      // Add new token at the beginning of trending
      const newTrendingTokens = [token, ...currentTrendingTokens].slice(0, MAX_DISPLAY_TOKENS);
      tokenCache['trending'] = newTrendingTokens;
      
      console.log(`🔥 PROMOTED TO TRENDING: ${token.symbol} (${token.mint.slice(0, 8)}...) - Curve: ${token.bonding_curve_progress}%, Market Cap: $${(token.market_cap_usd || 0).toLocaleString()}`);
      console.log(`✅ Transaction subscription maintained: ${token.buys_count || 0} buys, ${token.sells_count || 0} sells`);
      console.log(`⏰ 60-second grace period started`);
      
      // Dispatch event for trending category update
      window.dispatchEvent(new CustomEvent('ekko:token_feed_update', {
        detail: { category: 'trending', token, action: 'add' }
      }));
    } else {
      // Token already in trending - just ensure grace period is set
      markTokenEnteredTrending(token.mint, token.symbol);
    }
  },
  
  // 🔥 **NEW**: Enhanced market cap value finder
  findMarketCapValue(data: any): number | null {
    const possibleMarketCapFields = [
      // Direct fields - all variations
      data.marketCap?.usd, data.market_cap?.usd, data.marketcap?.usd,
      data.marketCapUsd, data.market_cap_usd, data.marketcapusd,
      data.marketCap, data.market_cap, data.marketcap, data.mcap, data.mc,
      
      // Nested in pool data
      data.pool?.marketCap?.usd, data.pool?.market_cap?.usd, data.pool?.marketcap?.usd,
      data.pool?.marketCapUsd, data.pool?.market_cap_usd, data.pool?.marketcapusd,
      data.pool?.marketCap, data.pool?.market_cap, data.pool?.marketcap, data.pool?.mcap, data.pool?.mc,
      
      // Nested in stats data
      data.stats?.marketCap?.usd, data.stats?.market_cap?.usd, data.stats?.marketcap?.usd,
      data.stats?.marketCapUsd, data.stats?.market_cap_usd, data.stats?.marketcapusd,
      data.stats?.marketCap, data.stats?.market_cap, data.stats?.marketcap, data.stats?.mcap, data.stats?.mc,
      
      // Nested in data field
      data.data?.marketCap?.usd, data.data?.market_cap?.usd, data.data?.marketcap?.usd,
      data.data?.marketCapUsd, data.data?.market_cap_usd, data.data?.marketcapusd,
      data.data?.marketCap, data.data?.market_cap, data.data?.marketcap, data.data?.mcap, data.data?.mc,
      
      // Nested in token field
      data.token?.marketCap?.usd, data.token?.market_cap?.usd, data.token?.marketcap?.usd,
      data.token?.marketCapUsd, data.token?.market_cap_usd, data.token?.marketcapusd,
      data.token?.marketCap, data.token?.market_cap, data.token?.marketcap, data.token?.mcap, data.token?.mc,
      
      // Nested in pools array
      data.pools?.[0]?.marketCap?.usd, data.pools?.[0]?.market_cap?.usd, data.pools?.[0]?.marketcap?.usd,
      data.pools?.[0]?.marketCapUsd, data.pools?.[0]?.market_cap_usd, data.pools?.[0]?.marketcapusd,
      data.pools?.[0]?.marketCap, data.pools?.[0]?.market_cap, data.pools?.[0]?.marketcap, data.pools?.[0]?.mcap, data.pools?.[0]?.mc,
      
      // Nested in market data
      data.market?.marketCap?.usd,
      data.market?.market_cap?.usd,
      data.market?.marketcap?.usd,
      data.market?.marketCapUsd,
      data.market?.market_cap_usd,
      data.market?.marketcapusd,
      data.market?.marketCap,
      data.market?.market_cap,
      data.market?.marketcap,
      data.market?.mcap,
      data.market?.mc
    ];
    
    for (const field of possibleMarketCapFields) {
      if (typeof field === 'number' && field > 0) {
        return field;
      }
    }
    
    return null;
  },
  
  // 🔥 **NEW**: Enhanced curve percentage value finder
  findCurvePercentageValue(data: any): number | null {
    const possibleCurveFields = [
      // Direct fields - all variations
      data.curvePercentage, data.curve_percentage, data.curvepercentage,
      data.curveprogress, data.curve_progress, data.lpBurn, data.lp_burn, data.lpburn,
      data.bondingCurve, data.bonding_curve, data.bondingcurve,
      data.curve, data.progress, data.percentage, data.percent, data.pct,
      
      // Nested in pool data
      data.pool?.curvePercentage, data.pool?.curve_percentage, data.pool?.curvepercentage,
      data.pool?.curveprogress, data.pool?.curve_progress, data.pool?.lpBurn, data.pool?.lp_burn, data.pool?.lpburn,
      data.pool?.bondingCurve, data.pool?.bonding_curve, data.pool?.bondingcurve,
      data.pool?.curve, data.pool?.progress, data.pool?.percentage, data.pool?.percent, data.pool?.pct,
      
      // Nested in data field
      data.data?.curvePercentage, data.data?.curve_percentage, data.data?.curvepercentage,
      data.data?.curveprogress, data.data?.curve_progress, data.data?.lpBurn, data.data?.lp_burn, data.data?.lpburn,
      data.data?.bondingCurve, data.data?.bonding_curve, data.data?.bondingcurve,
      data.data?.curve, data.data?.progress, data.data?.percentage, data.data?.percent, data.data?.pct,
      
      // Nested in token field
      data.token?.curvePercentage, data.token?.curve_percentage, data.token?.curvepercentage,
      data.token?.curveprogress, data.token?.curve_progress, data.token?.lpBurn, data.token?.lp_burn, data.token?.lpburn,
      data.token?.bondingCurve, data.token?.bonding_curve, data.token?.bondingcurve,
      data.token?.curve, data.token?.progress, data.token?.percentage, data.token?.percent, data.token?.pct,
      
      // Nested in pools array
      data.pools?.[0]?.curvePercentage, data.pools?.[0]?.curve_percentage, data.pools?.[0]?.curvepercentage,
      data.pools?.[0]?.curveprogress, data.pools?.[0]?.curve_progress, data.pools?.[0]?.lpBurn, data.pools?.[0]?.lp_burn, data.pools?.[0]?.lpburn,
      data.pools?.[0]?.bondingCurve, data.pools?.[0]?.bonding_curve, data.pools?.[0]?.bondingcurve,
      data.pools?.[0]?.curve, data.pools?.[0]?.progress, data.pools?.[0]?.percentage, data.pools?.[0]?.percent, data.pools?.[0]?.pct,
      
      // Nested in stats data
      data.stats?.curvePercentage, data.stats?.curve_percentage, data.stats?.curvepercentage,
      data.stats?.curveprogress, data.stats?.curve_progress, data.stats?.lpBurn, data.stats?.lp_burn, data.stats?.lpburn,
      data.stats?.bondingCurve, data.stats?.bonding_curve, data.stats?.bondingcurve,
      data.stats?.curve, data.stats?.progress, data.stats?.percentage, data.stats?.percent, data.stats?.pct,
      
      // Nested in market data
      data.market?.curvePercentage, data.market?.curve_percentage, data.market?.curvepercentage,
      data.market?.curveprogress, data.market?.curve_progress, data.market?.lpBurn, data.market?.lp_burn, data.market?.lpburn,
      data.market?.bondingCurve, data.market?.bonding_curve, data.market?.bondingcurve,
      data.market?.curve, data.market?.progress, data.market?.percentage, data.market?.percent, data.market?.pct
    ];
    
    for (const field of possibleCurveFields) {
      if (typeof field === 'number' && field >= 0 && field <= 100) {
        return field;
      }
    }
    
    return null;
  },
  
  cleanup() {
    console.log('🧹 Cleaning up enhanced global subscriptions...');
    
    // Clean up category subscriptions
    (Object.keys(this.subscriptions) as CategoryType[]).forEach(category => {
      const sub = this.subscriptions[category];
      sub.unsubscribeFunctions.forEach((unsubscribe: () => void) => {
        try {
          unsubscribe();
    } catch (error) {
          console.error(`Error cleaning up ${category} subscription:`, error);
        }
      });
      sub.unsubscribeFunctions = [];
      sub.active = false;
    });
    
    // Clean up individual token subscriptions
    this.tokenSubscriptions.forEach((subscription, mint) => {
      subscription.unsubscribeFunctions.forEach(unsubscribe => {
        try {
          unsubscribe();
        } catch (error) {
          console.error(`Error cleaning up token subscription for ${mint}:`, error);
        }
      });
    });
    this.tokenSubscriptions.clear();
    
    this.initialized = false;
    console.log('✅ Enhanced global subscriptions cleanup complete');
  },

  // 🔥 **SIMPLIFIED**: Just increment buy/sell counts, no complex logic
  simpleIncrementCounts(tokenMint: string, buysIncrement: number, sellsIncrement: number) {
    const subscription = this.tokenSubscriptions.get(tokenMint);
    if (!subscription) return;
    
    // Update last seen time
    subscription.lastSeen = Date.now();
    
    // Update token in all relevant category caches
    subscription.categories.forEach(category => {
      const categoryTokens = tokenCache[category];
      if (!categoryTokens) return;
      
      const tokenIndex = categoryTokens.findIndex(t => t.mint === tokenMint);
      if (tokenIndex >= 0) {
        const token = { ...categoryTokens[tokenIndex] };
        
        // Simple increment - never decrease
        if (buysIncrement > 0) {
          token.buys_count = (token.buys_count || 0) + buysIncrement;
          token.buys = token.buys_count;
          console.log(`📈 ${token.symbol}: ${(token.buys_count || 0) - buysIncrement} + ${buysIncrement} = ${token.buys_count} buys`);
        }
        
        if (sellsIncrement > 0) {
          token.sells_count = (token.sells_count || 0) + sellsIncrement;
          token.sells = token.sells_count;
          console.log(`📉 ${token.symbol}: ${(token.sells_count || 0) - sellsIncrement} + ${sellsIncrement} = ${token.sells_count} sells`);
        }
        
        token.updated_at = new Date().toISOString();
        categoryTokens[tokenIndex] = token;
        
        // 🔥 **AUTO-PROMOTION CHECK**: Check if token should be promoted to trending
        this.checkAndPromoteToTrending(token, category);
        
        // Dispatch update event for this category
        window.dispatchEvent(new CustomEvent('ekko:token_feed_update', {
          detail: { category, token, action: 'update' }
        }));
      }
    });
  },

  // 🔥 **SIMPLIFIED**: Set initial counts from history - ONLY called once per token
  setInitialCounts(tokenMint: string, buys: number, sells: number, volume: number) {
    const subscription = this.tokenSubscriptions.get(tokenMint);
    if (!subscription) return;
    
    console.log(`📊 Setting INITIAL counts for ${tokenMint.slice(0, 8)}: ${buys} buys, ${sells} sells`);
    
    // Update token in all relevant category caches
    subscription.categories.forEach(category => {
      const categoryTokens = tokenCache[category];
      if (!categoryTokens) return;
      
      const tokenIndex = categoryTokens.findIndex(t => t.mint === tokenMint);
      if (tokenIndex >= 0) {
        const token = { ...categoryTokens[tokenIndex] };
        
        // Set initial counts - only if they're currently 0
        if ((token.buys_count || 0) === 0) {
          token.buys_count = buys;
          token.buys = buys;
        }
        if ((token.sells_count || 0) === 0) {
          token.sells_count = sells;
          token.sells = sells;
        }
        if ((token.volume_usd || 0) === 0) {
          token.volume_usd = volume;
          token.tx_volume = volume;
          token.volume = volume;
        }
        
        token.updated_at = new Date().toISOString();
        categoryTokens[tokenIndex] = token;
        
        console.log(`✅ Initial counts set for ${token.symbol}: ${token.buys_count} buys, ${token.sells_count} sells`);
        
        // 🔥 **AUTO-PROMOTION CHECK**: Check if token should be promoted to trending
        this.checkAndPromoteToTrending(token, category);
        
        // Dispatch update event for this category
        window.dispatchEvent(new CustomEvent('ekko:token_feed_update', {
          detail: { category, token, action: 'update' }
        }));
      }
    });
  },

  // 🔥 **AUTO-PROMOTION CHECK**: Check if token should be promoted to trending
  checkAndPromoteToTrending(token: any, category: CategoryType) {
    // Only promote from 'new' to 'trending'
    if (category !== 'new') return;
    
    const subscription = this.tokenSubscriptions.get(token.mint);
    if (!subscription) return;
    
    // Don't promote if already in trending
    if (subscription.categories.has('trending')) return;
    
    const curvePercentage = token.bonding_curve_progress || 0;
    const marketCapUSD = token.market_cap_usd || 0;
    
    // 🔥 **TRENDING CRITERIA**: Either curve > 25% OR market cap > $30,000 qualifies for trending
    const qualifiesForTrending = (
      curvePercentage < 100 && // Must not be migrated yet
      (curvePercentage > 25 || marketCapUSD > MIN_TRENDING_TOKEN_MARKET_CAP)
    );
    
    if (qualifiesForTrending) {
      console.log(`🔥 AUTO-PROMOTION: ${token.symbol} qualifies for trending! Curve: ${curvePercentage}%, Market Cap: $${marketCapUSD.toLocaleString()}`);
      console.log(`📈 Promoting with 60-second grace period protection`);
      this.promoteTokenToTrending(token);
    }
  },

  // 🔥 **SIMPLIFIED**: Handle token updates globally - only price/market cap, no transaction counts
  handleTokenUpdate(tokenMint: string, tokenSymbol: string, data: any) {
    try {
      if (!data || typeof data !== 'object') return;

      // Only handle price, liquidity, market cap updates - NOT transaction counts
      const hasRelevantUpdates = (
        data.price?.usd !== undefined ||
        data.liquidity?.usd !== undefined ||
        this.findMarketCapValue(data) !== null ||
        this.findCurvePercentageValue(data) !== null ||
        data.market
      );
      
      if (!hasRelevantUpdates) return;
      
      console.log(`💰 Price/market update for ${tokenSymbol}`);
      
      // Update token in all relevant caches - simple direct updates only
      const subscription = this.tokenSubscriptions.get(tokenMint);
      if (!subscription) return;
      
      subscription.categories.forEach(category => {
        const categoryTokens = tokenCache[category];
        if (!categoryTokens) return;
        
        const tokenIndex = categoryTokens.findIndex(t => t.mint === tokenMint);
        if (tokenIndex >= 0) {
          const token = { ...categoryTokens[tokenIndex] };
          
          // Handle price updates
          if (data.price?.usd !== undefined && typeof data.price.usd === 'number') {
            token.price_usd = data.price.usd;
            token.token_price = data.price.usd;
          }
          
          // Handle liquidity updates
          if (data.liquidity?.usd !== undefined && typeof data.liquidity.usd === 'number') {
            token.liquidity_usd = data.liquidity.usd;
            token.liquidity = data.liquidity.usd;
          }
          
          // Enhanced market cap detection
          let marketCapValue = this.findMarketCapValue(data);
          if (marketCapValue !== null) {
            token.market_cap_usd = marketCapValue;
          }
          
          // Enhanced curve percentage detection
          let curvePercentageValue = this.findCurvePercentageValue(data);
          if (curvePercentageValue !== null) {
            token.bonding_curve_progress = curvePercentageValue;
          }
          
          // Update market info
          if (data.market) {
            token.market = data.market;
            token.market_type = data.market;
            token.is_raydium = data.market === 'raydium';
            token.is_migrated = data.market === 'raydium';
          }
          
          token.updated_at = new Date().toISOString();
          categoryTokens[tokenIndex] = token;
          
          // 🔥 **AUTO-PROMOTION CHECK**: Check if token should be promoted to trending
          this.checkAndPromoteToTrending(token, category);
          
          // Dispatch update event
          window.dispatchEvent(new CustomEvent('ekko:token_feed_update', {
            detail: { category, token, action: 'update' }
          }));
        }
      });
    } catch (error) {
      console.error(`Error in token update for ${tokenMint}:`, error);
    }
  },

  // 🔥 **DEV SELL DETECTION**: Mark token as dev sold across all categories
  markTokenAsDevSold(tokenMint: string, tokenSymbol: string) {
    const subscription = this.tokenSubscriptions.get(tokenMint);
    if (!subscription) return;
    
    const devSellTime = new Date().toISOString();
    console.log(`🚨 MARKING TOKEN AS DEV SOLD: ${tokenSymbol} at ${devSellTime}`);
    
    // Update token in all relevant category caches
    subscription.categories.forEach(category => {
      const categoryTokens = tokenCache[category];
      if (!categoryTokens) return;
      
      const tokenIndex = categoryTokens.findIndex(t => t.mint === tokenMint);
      if (tokenIndex >= 0) {
        const token = { ...categoryTokens[tokenIndex] };
        
        // Mark as dev sold
        token.dev_sell_time = devSellTime;
        token.updated_at = devSellTime;
        
        categoryTokens[tokenIndex] = token;
        
        console.log(`🔴 Token ${tokenSymbol} marked as dev sold in ${category} category`);
        
        // Dispatch update event for this category
        window.dispatchEvent(new CustomEvent('ekko:token_feed_update', {
          detail: { category, token, action: 'update' }
        }));
      }
    });
  },
};

// Initialize global subscriptions when the module loads
if (typeof window !== 'undefined') {
  // Delay initialization slightly to ensure other services are ready
  setTimeout(() => {
    globalSubscriptionManager.initializeAllSubscriptions();
  }, 1000);
  
  // 🔥 **TRENDING GRACE PERIOD CLEANUP**: Clean up old entries every 5 minutes
  setInterval(() => {
    const now = Date.now();
    let cleanedCount = 0;
    
    for (const [tokenMint, entryTime] of trendingGracePeriod.entries()) {
      // Remove entries older than 10 minutes (well past the 60-second grace period)
      if (now - entryTime > 10 * 60 * 1000) {
        trendingGracePeriod.delete(tokenMint);
        cleanedCount++;
      }
    }
    
    if (cleanedCount > 0) {
      console.log(`🧹 Cleaned up ${cleanedCount} old trending grace period entries`);
    }
  }, 5 * 60 * 1000); // Run every 5 minutes
}

// 🔧 **SIMPLE TOKEN TRACKING** - Track which tokens we've already processed
const processedTokens = new Set<string>();
const tokenHistoryFetched = new Set<string>();

// 🔥 **SIMPLE TRANSACTION HISTORY** - Get history ONCE per token, set initial counts
const fetchTokenTransactionHistoryOnce = async (tokenMint: string): Promise<{ buys: number; sells: number; volume: number }> => {
  // Check if we already fetched history for this token
  if (tokenHistoryFetched.has(tokenMint)) {
    console.log(`⏭️ History already fetched for ${tokenMint.slice(0, 8)}, skipping`);
    return { buys: 0, sells: 0, volume: 0 };
  }

  console.log(`📊 Fetching transaction history ONCE for ${tokenMint.slice(0, 8)}...`);
  tokenHistoryFetched.add(tokenMint);

  try {
    const result = await getTokenTransactions(tokenMint, 100);
    
    if (result.status === 'success' && result.data?.trades) {
      const trades = result.data.trades;
      
      let buysCount = 0;
      let sellsCount = 0;
      let totalVolume = 0;
      
      trades.forEach((trade: any) => {
        if (trade.type === 'buy') {
          buysCount++;
        } else if (trade.type === 'sell') {
          sellsCount++;
        }
        
        if (trade.volume_usd && typeof trade.volume_usd === 'number') {
          totalVolume += trade.volume_usd;
        }
      });
      
      console.log(`📈 INITIAL counts for ${tokenMint.slice(0, 8)}: ${buysCount} buys, ${sellsCount} sells, $${totalVolume.toFixed(2)} volume`);
      return { buys: buysCount, sells: sellsCount, volume: totalVolume };
    }
  } catch (error) {
    console.error(`Error fetching history for ${tokenMint}:`, error);
  }
  
  return { buys: 0, sells: 0, volume: 0 };
};

// 🔧 **DEV SELL TRACKING** - Track deployer addresses for dev sell detection
const tokenDeployers = new Map<string, string>(); // tokenMint -> deployerAddress

// 🔥 **DEV SELL DETECTION** - Check if a transaction is from the token's deployer
const checkForDevSell = (tokenMint: string, transaction: any): boolean => {
  const deployerAddress = tokenDeployers.get(tokenMint);
  
  if (!deployerAddress) {
    console.log(`⚠️ No deployer address tracked for ${tokenMint.slice(0, 8)}`);
    return false;
  }
  
  // Check if this transaction is a sell from the deployer
  if (transaction.type === 'sell' && transaction.wallet === deployerAddress) {
    console.log(`🚨 DEV SELL DETECTED: ${tokenMint.slice(0, 8)} - Deployer ${deployerAddress.slice(0, 8)} sold!`);
    return true;
  }
  
  // Also check alternative field names for wallet address
  const walletFields = [
    transaction.wallet,
    transaction.walletAddress, 
    transaction.user,
    transaction.trader,
    transaction.from,
    transaction.seller
  ];
  
  for (const walletField of walletFields) {
    if (walletField === deployerAddress && transaction.type === 'sell') {
      console.log(`🚨 DEV SELL DETECTED: ${tokenMint.slice(0, 8)} - Deployer ${deployerAddress.slice(0, 8)} sold! (via ${Object.keys(transaction).find(k => transaction[k] === walletField)})`);
      return true;
    }
  }
  
  return false;
};

// 🔧 **TRENDING GRACE PERIOD** - Track when tokens enter trending to prevent immediate removal
const trendingGracePeriod = new Map<string, number>(); // tokenMint -> timestamp when entered trending
const TRENDING_GRACE_PERIOD_MS = 60 * 1000; // 60 seconds

// 🔥 **TRENDING GRACE PERIOD CHECK** - Check if token should stay in trending due to grace period
const isInTrendingGracePeriod = (tokenMint: string): boolean => {
  const entryTime = trendingGracePeriod.get(tokenMint);
  if (!entryTime) return false;
  
  const timeSinceEntry = Date.now() - entryTime;
  return timeSinceEntry < TRENDING_GRACE_PERIOD_MS;
};

// 🔥 **MARK TOKEN AS ENTERED TRENDING** - Record when token first enters trending
const markTokenEnteredTrending = (tokenMint: string, tokenSymbol: string) => {
  if (!trendingGracePeriod.has(tokenMint)) {
    trendingGracePeriod.set(tokenMint, Date.now());
    console.log(`⏰ Started 60s grace period for ${tokenSymbol} in trending`);
  }
};

const TokenList: React.FC<TokenListProps> = ({ category, searchTerm = '', onPauseFeed, onResumeFeed, containerClassName }) => {
  const [tokens, setTokens] = useState<Token[]>([]);
  const [frozenTokens, setFrozenTokens] = useState<Token[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [visibleIndices, setVisibleIndices] = useState<{ start: number; end: number }>({ start: 0, end: PAGE_SIZE });
  const [wsStatus, setWsStatus] = useState<'connected' | 'connecting' | 'disconnected'>('disconnected');
  const [isHovering, setIsHovering] = useState(false);
  const [showPauseIcon, setShowPauseIcon] = useState(false);

  const containerRef = useRef<HTMLDivElement>(null);
  const unsubscribeRef = useRef<(() => void) | null>(null);

  // 🔥 **SIMPLIFIED**: Fetch transaction history ONCE per token, set initial counts
  const fetchTokenTransactionHistory = useCallback(async (tokenMint: string) => {
    // Use the global tracking function to ensure we only fetch once per token
    const historyData = await fetchTokenTransactionHistoryOnce(tokenMint);
    
    if (historyData.buys > 0 || historyData.sells > 0 || historyData.volume > 0) {
      // Set initial counts via global manager
      globalSubscriptionManager.setInitialCounts(tokenMint, historyData.buys, historyData.sells, historyData.volume);
    }
  }, []);

  // 🔥 **SUBSCRIBE TO INDIVIDUAL TOKEN UPDATES** 
  // This is now handled by the global subscription manager to prevent conflicts
  const subscribeToTokenUpdates = useCallback((tokenMint: string, tokenName: string, tokenSymbol: string) => {
    // Delegate to global subscription manager to avoid conflicts
    globalSubscriptionManager.subscribeToTokenUpdates(tokenMint, tokenName, tokenSymbol, category);
  }, [category]);

  // 🔥 **CLEANUP SUBSCRIPTIONS** - Now handled by global manager
  const cleanupCategorySubscriptions = useCallback(() => {
    // No local subscriptions to clean up - everything is handled by global manager
    console.log(`🧹 Local cleanup for ${category} (global manager handles actual subscriptions)`);
  }, [category]);

  // 🔥 **MAIN EFFECT**: Use global subscriptions and listen for feed updates
  useEffect(() => {
    // Load cached tokens immediately if available
    const cachedDisplayTokens = tokenCache[category];
    if (cachedDisplayTokens && cachedDisplayTokens.length > 0) {
      console.log(`📊 Loading ${cachedDisplayTokens.length} cached tokens for ${category}`);
      setTokens(cachedDisplayTokens);
      setLoading(false);
    } else {
      setTokens([]);
      setLoading(categoryLoadingStates[category]);
    }

    // Ensure global subscriptions are initialized
    globalSubscriptionManager.initializeAllSubscriptions().then(() => {
      setWsStatus('connected');
      setError(null);
      setLoading(false);
      categoryLoadingStates[category] = false;
    }).catch((error) => {
      console.error(`Failed to initialize global subscriptions:`, error);
      setError(`Failed to connect to real-time data: ${error}`);
      setLoading(false);
      setWsStatus('disconnected');
    });

    // Listen for global feed updates for this category
    const handleFeedUpdate = (event: CustomEvent<{category: string, token: any, action: string}>) => {
      const { category: updateCategory, token: updatedToken, action } = event.detail;
      
      // Only process updates for our category
      if (updateCategory === category) {
        console.log(`📱 Received ${action} for ${category}: ${updatedToken.symbol}`);
        
        // Simply refresh from cache - the global manager has already updated it
        const cachedTokens = tokenCache[category] || [];
        setTokens(cachedTokens.slice(0, MAX_DISPLAY_TOKENS));
        
        // 🔥 **CRITICAL FIX**: Ensure ALL tokens have transaction tracking, regardless of action
        const tokenName = updatedToken.name || 'Unknown';
        const tokenSymbol = updatedToken.symbol || 'UNKNOWN';
        
        // 🔥 **TRENDING GRACE PERIOD**: Mark tokens entering trending
        if (category === 'trending' && action === 'add') {
          markTokenEnteredTrending(updatedToken.mint, tokenSymbol);
        }
        
        // Check if this token needs transaction processing
        const needsProcessing = (
          action === 'add' || // New tokens always need processing
          !globalSubscriptionManager.tokenSubscriptions.has(updatedToken.mint) || // No subscription exists
          !tokenHistoryFetched.has(updatedToken.mint) // History not fetched yet
        );
        
        if (needsProcessing) {
          const hasSubscription = globalSubscriptionManager.tokenSubscriptions.has(updatedToken.mint);
          const hasHistory = tokenHistoryFetched.has(updatedToken.mint);
          
          console.log(`🔄 Processing transaction tracking for ${tokenSymbol} in ${category}:`);
          console.log(`   - Action: ${action}`);
          console.log(`   - Has subscription: ${hasSubscription}`);
          console.log(`   - Has history: ${hasHistory}`);
          console.log(`   - Current counts: ${updatedToken.buys_count || 0} buys, ${updatedToken.sells_count || 0} sells`);
          
          // 🔥 **ENSURE TRENDING TOKENS MAINTAIN SUBSCRIPTIONS**
          if (category === 'trending') {
            console.log(`📈 Ensuring trending token ${tokenSymbol} maintains full subscription`);
          }
          
          // Fetch historical data FIRST, then subscribe to updates
          fetchTokenTransactionHistory(updatedToken.mint).then(() => {
            // Only subscribe to websocket updates AFTER historical data is loaded
            subscribeToTokenUpdates(updatedToken.mint, tokenName, tokenSymbol);
          });
        } else {
          console.log(`✅ Token ${tokenSymbol} already has transaction tracking: ${updatedToken.buys_count || 0} buys, ${updatedToken.sells_count || 0} sells`);
        }
      }
    };

    // Add event listener for global feed updates
    window.addEventListener('ekko:token_feed_update', handleFeedUpdate as EventListener);

    // Cleanup listener when component unmounts or category changes
    return () => {
      window.removeEventListener('ekko:token_feed_update', handleFeedUpdate as EventListener);
    };
  }, [category, subscribeToTokenUpdates, fetchTokenTransactionHistory]);

  // Handle refresh
  const handleRefresh = useCallback(async () => {
    console.log('Refreshing WebSocket connection and resubscribing');
    
    tokenCache[category] = [];
    categoryLoadingStates[category] = true;
    
    setTokens([]);
    setLoading(true);
    setError(null);

    cleanupCategorySubscriptions();
    
    try {
      await websocketService.connect();
      // The useEffect will handle resubscription
    } catch (error) {
      console.error('Failed to refresh WebSocket connection:', error);
      setError('Failed to reconnect to real-time data');
      setLoading(false);
    }
  }, [category, cleanupCategorySubscriptions]);

  // Handle token selection
  const handleSelectToken = useCallback((token: Token) => {
    const event = new CustomEvent('ekko:token_selected', { 
      detail: { token, force_refresh: true }
    });
    window.dispatchEvent(event);
    
    const canvasUpdateEvent = new CustomEvent('ekko:canvas_update_widgets', { 
      detail: { token, timestamp: Date.now() }
    });
    window.dispatchEvent(canvasUpdateEvent);
  }, []);

  // Filter tokens based on search term
  const filteredTokens = useMemo(() => {
    const displayTokens = isHovering ? frozenTokens : tokens;
    
    let filtered = searchTerm 
      ? displayTokens.filter(token => 
          token.name?.toLowerCase().includes(searchTerm.toLowerCase()) || 
          token.symbol?.toLowerCase().includes(searchTerm.toLowerCase()) ||
          token.mint.toLowerCase() === searchTerm.toLowerCase()
        ).slice(0, 25)
      : displayTokens.slice(0, 25);

    // Filter based on category requirements
    filtered = filtered.filter(token => {
      const curvePercentage = token.bonding_curve_progress || 0;
      const marketCapUSD = token.market_cap_usd || 0;
      
      switch (category) {
        case 'new':
          // 🔥 **ADDITIONAL MARKET CAP FILTER**: Double-check at display level
          if (marketCapUSD > MAX_NEW_TOKEN_MARKET_CAP) {
            console.log(`🚫 Display filter: Removing ${token.symbol} from new feed - Market cap: $${marketCapUSD.toLocaleString()}`);
            return false;
          }
          return true;
        case 'trending':
          // 🔥 **GRACE PERIOD PROTECTION**: Keep tokens in trending for 60 seconds even if they don't meet criteria
          if (isInTrendingGracePeriod(token.mint)) {
            console.log(`⏰ Grace period: Keeping ${token.symbol} in trending for ${Math.ceil((TRENDING_GRACE_PERIOD_MS - (Date.now() - (trendingGracePeriod.get(token.mint) || 0))) / 1000)}s more`);
            return curvePercentage < 100; // Still must not be migrated
          }
          
          // 🔥 **UPDATED TRENDING CRITERIA**: Curve percentage > 25% OR market cap > $30,000 (Either condition qualifies)
          if (marketCapUSD < MIN_TRENDING_TOKEN_MARKET_CAP && curvePercentage <= 25) {
            console.log(`🚫 Display filter: Removing ${token.symbol} from trending feed - Neither condition met: Curve: ${curvePercentage}%, Market Cap: $${marketCapUSD.toLocaleString()}`);
            // Remove from grace period tracking when token leaves trending
            trendingGracePeriod.delete(token.mint);
            return false;
          }
          return curvePercentage < 100; // Must not be migrated
        case 'migrated':
          return curvePercentage >= 100 || token.is_migrated || token.market === 'raydium';
        case 'graduating':
          return curvePercentage >= 80 && curvePercentage < 100;
        case 'graduated':
          return curvePercentage >= 100 || token.is_migrated || token.market === 'raydium';
        default:
          return true;
      }
    });

    return filtered
      .filter((token, index, self) => index === self.findIndex(t => t.mint === token.mint))
      .slice(0, 25);
  }, [tokens, frozenTokens, isHovering, searchTerm, category]);
    
  // Calculate visible tokens for virtual scrolling
  const visibleTokens = useMemo(() => {
    return filteredTokens.slice(visibleIndices.start, Math.min(visibleIndices.end + 1, 25));
  }, [filteredTokens, visibleIndices.start, visibleIndices.end]);

  // Handle mouse events for pausing feed
  const handleMouseEnter = () => {
    setIsHovering(true);
    setShowPauseIcon(true);
    setFrozenTokens(tokens);
    if (onPauseFeed) onPauseFeed();
  };

  const handleMouseLeave = () => {
    setIsHovering(false);
    setTimeout(() => {
      setShowPauseIcon(false);
    }, 500);
    setFrozenTokens([]);
    if (onResumeFeed) onResumeFeed();
  };

  // Update visible items on scroll
  const updateVisibleItems = useCallback(() => {
    if (!containerRef.current) return;
    
    const container = containerRef.current;
    const scrollTop = container.scrollTop;
    const clientHeight = container.clientHeight;
    const itemHeight = 90;
    const buffer = 2;
    
    const startIndex = Math.max(0, Math.floor(scrollTop / itemHeight) - buffer);
    const endIndex = Math.min(
      Math.min(filteredTokens.length - 1, 24),
      Math.ceil((scrollTop + clientHeight) / itemHeight) + buffer
    );
    
    setVisibleIndices({ start: startIndex, end: endIndex });
  }, [filteredTokens.length]);

  // Handle scroll events
  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    const handleScroll = () => {
      updateVisibleItems();
    };

    container.addEventListener('scroll', handleScroll);
    updateVisibleItems();
    
    return () => container.removeEventListener('scroll', handleScroll);
  }, [updateVisibleItems]);

  if (loading) {
    return (
      <div className="flex flex-col items-center justify-center py-4">
        <Loader className="w-4 h-4 text-primary animate-spin mb-2" />
        <p className="text-[10px] text-gray-400">
          {wsStatus === 'connecting' ? 'Connecting to SolanaTracker...' : 'Loading tokens...'}
        </p>
        <p className="text-[8px] text-gray-500 mt-1">
          WebSocket: {wsStatus}
        </p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex flex-col items-center justify-center py-4 text-center">
        <AlertCircle className="w-4 h-4 text-red-400 mb-1" />
        <p className="text-xs text-red-400 mb-1">{error}</p>
        <p className="text-[8px] text-gray-500 mb-2">
          WebSocket: {wsStatus}
        </p>
        <button 
          className="text-[10px] glass-button py-1 px-2 flex items-center gap-1"
          onClick={handleRefresh}
        >
          <RefreshCw size={8} />
          Reconnect
        </button>
      </div>
    );
  }

  if (filteredTokens.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center py-4 text-center">
        <p className="text-xs text-gray-400 mb-1">No tokens found</p>
        {searchTerm && (
          <p className="text-[10px] text-gray-500 mb-2">
            Try adjusting your search terms
          </p>
        )}
        <p className="text-[8px] text-gray-500 mb-2">
          WebSocket: {wsStatus} | Waiting for real-time data...
        </p>
        
        <button 
          className="text-[10px] glass-button py-1 px-2 flex items-center gap-1"
          onClick={handleRefresh}
        >
          <RefreshCw size={8} />
          Refresh Connection
        </button>
      </div>
    );
  }

  return (
    <div 
              className={`w-full max-w-[380px] relative token-list-container ${containerClassName || ''}`}
      ref={containerRef}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
      onScroll={updateVisibleItems}
      style={{
        overflow: 'auto',
        scrollbarWidth: 'none',
        msOverflowStyle: 'none',
      }}
    >
      <style>
        {`
          .token-list-container::-webkit-scrollbar {
            display: none;
          }
          .token-list-container {
            scrollbar-width: none;
            -ms-overflow-style: none;
          }
        `}
      </style>
      
      {/* WebSocket Status Indicator */}
      <div className="absolute top-1 right-1 z-10">
        <div className={`w-2 h-2 rounded-full ${
          wsStatus === 'connected' ? 'bg-green-500' : 
          wsStatus === 'connecting' ? 'bg-yellow-500 animate-pulse' : 
          'bg-red-500'
        }`} title={`SolanaTracker WebSocket: ${wsStatus}`} />
      </div>
      
      <div style={{ position: 'relative', height: `${Math.min(filteredTokens.length, 25) * 90}px` }}>
        {visibleTokens.map((token, visibleIndex) => {
          const actualIndex = visibleIndices.start + visibleIndex;
          const cardHeight = 90;
          
          return (
            <div 
              key={token.mint} 
              style={{ 
                position: 'absolute', 
                top: `${actualIndex * cardHeight}px`, 
                width: '100%', 
                height: `${cardHeight}px` 
              }}
            >
              <MemoizedTokenCard 
                token={token} 
                category={category} 
                onSelect={() => handleSelectToken(token)} 
              />
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default TokenList;